import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../api/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in and token is valid
    const storedToken = localStorage.getItem('medical-token');
    const storedUser = localStorage.getItem('medical-user');

    if (storedToken && authService.isAuthenticated()) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    } else {
      // Token expired or invalid - ensure cleanup
      localStorage.removeItem('medical-token');
      localStorage.removeItem('medical-user');
    }
    setLoading(false);

    // Optional: Set up periodic token validation (every 5 minutes)
    const tokenCheckInterval = setInterval(() => {
      if (!authService.isAuthenticated()) {
        // Token expired - logout and redirect
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(tokenCheckInterval);
  }, []);

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    return response;
  };

  const verifyOTP = async (email, otpToken) => {
    const response = await authService.verifyOTP(email, otpToken);

    // Store token and user
    localStorage.setItem('medical-token', response.accessToken);
    localStorage.setItem('medical-user', JSON.stringify(response.user));

    setToken(response.accessToken);
    setUser(response.user);
    setIsAuthenticated(true);

    return response;
  };

  const logout = async () => {
    if (user) {
      await authService.logout(user.email);
    }

    localStorage.removeItem('medical-token');
    localStorage.removeItem('medical-user');

    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    verifyOTP,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
